import React from 'react';
import { useParams } from 'react-router-dom';

import * as GQL from '~/graphql/generated/client';

/**
 * @deprecated Use `useControlVisibility` hook instead.
 *
 * This legacy hook uses the old visibility logic based on `visibilityType` and `nextControl`
 * fields on Control objects. The new `useControlVisibility` hook uses the UserJourney
 * configuration for more flexible conditional logic with:
 * - Multi-parent dependencies (AND logic)
 * - OR logic for answer options within a condition
 * - Inverse conditions (show if NOT selected)
 *
 * Migration:
 * ```tsx
 * // Old:
 * const { visibleControls } = useConditionalControls({ bundleHelper });
 *
 * // New:
 * const { visibleControlIds } = useControlVisibility({
 *   policyIdentifier: assessment.policy.identifier,
 *   orgId: project.orgId,
 *   bundleId,
 *   answerBundle,
 *   controls: bundle.controls,
 * });
 * ```
 */
interface UseConditionalControlsProps {
  bundleHelper: (
    bundleId: string,
    versionId: string
  ) => {
    getAnswerValues: (controlIdentifier: string) => GQL.AnswerValueInput[];
    bundle?: {
      controls: GQL.Control[];
      _id: string;
    } | null;
  };
  bundleIdParam?: string;
  versionIdParam?: string;
  /** Project-level screening visibility map — controls with visible:false are excluded */
  screeningVisibility?: Record<
    string,
    { visible: boolean; reason?: string; ruleId?: string }
  > | null;
}

export function useConditionalControls({
  bundleHelper,
  screeningVisibility,
}: UseConditionalControlsProps) {
  const { bundleId, versionId } = useParams();
  const [visibleControls, setVisibleControls] = React.useState<Set<string>>(
    new Set()
  );

  const updateVisibleControls = React.useCallback(
    (controls: GQL.Control[]) => {
      const newVisibleControls = new Set<string>();

      controls.forEach((control) => {
        // Apply screening pre-filter first
        if (screeningVisibility) {
          const entry = screeningVisibility[control.identifier];
          if (entry && entry.visible === false) {
            return;
          }
        }

        if (
          !control.visibilityType ||
          control.visibilityType === 'INDEPENDENT'
        ) {
          newVisibleControls.add(control.identifier);
        }

        const answerValues = bundleHelper(
          bundleId ?? '',
          versionId ?? ''
        ).getAnswerValues(control.identifier);

        if (answerValues.length > 0) {
          newVisibleControls.add(control.identifier);

          if (control.answerType === GQL.AnswerType.ScoreCheckbox) {
            const lastAnswerValue = answerValues[answerValues.length - 1];
            const lastSelectedOption = control.answerOptions?.find(
              (opt) => opt._id === lastAnswerValue.answerOptionId
            );
            if (lastSelectedOption?.nextControl) {
              newVisibleControls.add(lastSelectedOption.nextControl);
            }
          } else {
            const selectedOption = control.answerOptions?.find(
              (opt) => opt._id === answerValues[0].answerOptionId
            );
            if (selectedOption?.nextControl) {
              newVisibleControls.add(selectedOption.nextControl);
            }
          }
        }
      });

      setVisibleControls(newVisibleControls);
    },
    [bundleId, versionId, bundleHelper, screeningVisibility]
  );

  const bundleData = React.useMemo(() => {
    if (!bundleId) {
      return null;
    }
    return bundleHelper(bundleId, versionId ?? '');
  }, [bundleHelper, bundleId, versionId]);

  // Keep ref to latest callback so effect can depend only on bundle id and avoid
  // re-running when bundleHelper/updateVisibleControls identity changes (which would
  // cause setState every render and "Maximum update depth exceeded").
  const updateVisibleControlsRef = React.useRef(updateVisibleControls);
  updateVisibleControlsRef.current = updateVisibleControls;

  // Handle bundle data changes. Do not setState in cleanup—it triggers
  // "Maximum update depth exceeded" during navigation unmount and can break back navigation.
  React.useEffect(() => {
    if (bundleData?.bundle?.controls) {
      updateVisibleControlsRef.current(bundleData.bundle.controls);
    }
  }, [bundleData?.bundle?._id]);

  return {
    visibleControls,
    updateVisibleControls,
  };
}
